import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { modelsApi } from '@/lib/api/models'
import { useToast } from '@/lib/hooks/use-toast'
import { CreateModelRequest, ModelDefaults, ProviderSettingsUpdate } from '@/lib/types/models'
import { useI18n } from '@/lib/hooks/use-i18n'

export const MODEL_QUERY_KEYS = {
  models: ['models'] as const,
  model: (id: string) => ['models', id] as const,
  defaults: ['models', 'defaults'] as const,
  providers: ['models', 'providers'] as const,
  providerSettings: ['models', 'providers', 'settings'] as const,
}

export function useModels() {
  return useQuery({
    queryKey: MODEL_QUERY_KEYS.models,
    queryFn: () => modelsApi.list(),
  })
}

export function useModel(id: string) {
  return useQuery({
    queryKey: MODEL_QUERY_KEYS.model(id),
    queryFn: () => modelsApi.get(id),
    enabled: !!id,
  })
}

export function useCreateModel() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { t } = useI18n()

  return useMutation({
    mutationFn: (data: CreateModelRequest) => modelsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MODEL_QUERY_KEYS.models })
      toast({
        title: t('models.toast.create.success.title'),
        description: t('models.toast.create.success.description'),
      })
    },
    onError: (error: unknown) => {
      const errorMessage = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail || t('models.toast.create.error.description')
      toast({
        title: t('models.toast.create.error.title'),
        description: errorMessage,
        variant: 'destructive',
      })
    },
  })
}

export function useDeleteModel() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { t } = useI18n()

  return useMutation({
    mutationFn: (id: string) => modelsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MODEL_QUERY_KEYS.models })
      queryClient.invalidateQueries({ queryKey: MODEL_QUERY_KEYS.defaults })
      toast({
        title: t('models.toast.delete.success.title'),
        description: t('models.toast.delete.success.description'),
      })
    },
    onError: () => {
      toast({
        title: t('models.toast.delete.error.title'),
        description: t('models.toast.delete.error.description'),
        variant: 'destructive',
      })
    },
  })
}

export function useModelDefaults() {
  return useQuery({
    queryKey: MODEL_QUERY_KEYS.defaults,
    queryFn: () => modelsApi.getDefaults(),
  })
}

export function useUpdateModelDefaults() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { t } = useI18n()

  return useMutation({
    mutationFn: (data: Partial<ModelDefaults>) => modelsApi.updateDefaults(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MODEL_QUERY_KEYS.defaults })
      toast({
        title: t('models.toast.updateDefaults.success.title'),
        description: t('models.toast.updateDefaults.success.description'),
      })
    },
    onError: () => {
      toast({
        title: t('models.toast.updateDefaults.error.title'),
        description: t('models.toast.updateDefaults.error.description'),
        variant: 'destructive',
      })
    },
  })
}

export function useProviders() {
  return useQuery({
    queryKey: MODEL_QUERY_KEYS.providers,
    queryFn: () => modelsApi.getProviders(),
  })
}

export function useProviderSettings() {
  return useQuery({
    queryKey: MODEL_QUERY_KEYS.providerSettings,
    queryFn: () => modelsApi.getProviderSettings(),
  })
}

export function useUpdateProviderSettings() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { t } = useI18n()

  return useMutation({
    mutationFn: (data: ProviderSettingsUpdate) => modelsApi.updateProviderSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MODEL_QUERY_KEYS.providers })
      queryClient.invalidateQueries({ queryKey: MODEL_QUERY_KEYS.providerSettings })
      toast({
        title: t('models.toast.updateProviderSettings.success.title'),
        description: t('models.toast.updateProviderSettings.success.description'),
      })
    },
    onError: () => {
      toast({
        title: t('models.toast.updateProviderSettings.error.title'),
        description: t('models.toast.updateProviderSettings.error.description'),
        variant: 'destructive',
      })
    },
  })
}
